import { AssetType, GraphPosition } from '@arcxgame/contracts/dist/arc-types/core'
import { useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, BigNumberish, constants } from 'ethers'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import Confetti from 'react-confetti'
import { useLocation } from 'react-router-dom'
import { BASE } from 'src/constants/crypto'
import { ArcContext } from 'src/contexts/ArcContext'
import { calculateCollateralRatio } from 'src/helpers/calculateCollateralRatio'
import { calculateLiquidationDetails } from 'src/helpers/calculateLiquidationDetails'
import { calculateLiquidationPrice } from 'src/helpers/calculateLiquidationPrice'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useConfetti } from 'src/hooks/useConfetti'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import { Synth } from 'src/services/GraphService'
import { Spinner } from '../Spinner'
import { getDescription } from './getDescription'
import { Manage } from './Manage'
import './Manage.scss'

const { Zero } = constants

interface AmountChange {
  change: BigNumber
  isPositive: boolean
}

interface IProps {
  position: GraphPosition
  synth: Synth
}

interface ILocationState {
  isSucceed?: boolean
}

export function ManageExisting({ position, synth }: IProps) {
  const { arc, notifyInstance, getPrice, getSyntheticBalance, setIsLoading, synths } =
    useContext(ArcContext)
  const location = useLocation<ILocationState>()
  const { confetti } = useConfetti(location.state?.isSucceed)

  const collateralDetails = useErc20Details(synth.collateralAddress, arc?.signer)
  const syntheticDetails = useErc20Details(synth.syntheticAddress, arc?.signer)

  const collateralBalance = useErc20Balance(synth.collateralAddress, arc?.signer)
  const syntheticBalance = getSyntheticBalance(synth.syntheticAddress)
  const price = getPrice(synth.oracle) || constants.Zero

  const outstandingDebt = BigNumber.from(position.borrowedAmountValue)
  const debtUnpayable = syntheticBalance.lt(outstandingDebt)

  const [enteredDebtAmount, setEnteredDebtAmount] = useState<BigNumber>(
    BigNumber.from(position.borrowedAmountValue),
  )

  const positionCollateralValue = useMemo(() => {
    if (!collateralDetails) {
      return constants.Zero
    }
    return BigNumber.from(position.collateralAmountValue)
      .mul(BigNumber.from(10).pow(collateralDetails.decimals))
      .div(constants.WeiPerEther)
  }, [position.collateralAmountValue, collateralDetails])
  const [enteredCollateralAmount, setEnteredCollateralAmount] =
    useState<BigNumber>(positionCollateralValue)

  const [debtAmountChanged, setDebtAmountChanged] = useState({
    change: Zero,
    isPositive: false,
  })

  const [collateralAmountChanged, setCollateralAmountChanged] = useState({
    change: Zero,
    isPositive: false,
  })

  const positionId = BigNumber.from(position.id.split('-')[1])
  const liquidationDetails = calculateLiquidationDetails(position, price, synth, collateralDetails)

  useEffect(() => {
    setDebtAmountChanged(getChangedValue(enteredDebtAmount, position.borrowedAmountValue))
  }, [enteredDebtAmount, position.borrowedAmountValue])

  useEffect(() => {
    setCollateralAmountChanged(getChangedValue(enteredCollateralAmount, positionCollateralValue))
  }, [enteredCollateralAmount, positionCollateralValue])

  useEffect(() => {
    setEnteredCollateralAmount(positionCollateralValue)
  }, [positionCollateralValue])

  useEffect(() => {
    setEnteredDebtAmount(BigNumber.from(position.borrowedAmountValue))
  }, [position.borrowedAmountValue])

  if (!collateralDetails || !syntheticDetails) {
    return <Spinner />
  }

  const onActionClicked = async () => {
    try {
      setIsLoading(true)

      const borrow = (collateralChange: BigNumber, debtChange: BigNumber) =>
        arc.borrow(positionId, collateralChange, debtChange, synth.name)
      const repay = (collateralChange: BigNumber, debtChange: BigNumber) => {
        confirmRepay()
        arc.repay(positionId, debtChange, collateralChange, synth.name, undefined, {
          gasLimit: 1000000,
        })
      }
      logChanges(collateralAmountChanged, debtAmountChanged)

      // Deposit and borrow transaction
      const positiveCollateralChange = isPositiveChange(collateralAmountChanged)
      const positiveDebtAmountChange = isPositiveChange(debtAmountChanged)
      console.log({ positiveCollateralChange, positiveDebtAmountChange })
      if (positiveCollateralChange) {
        await arc.approveStableShare(collateralAmountChanged.change, synth.name)
      }
      if (positiveCollateralChange && positiveDebtAmountChange) {
        await borrow(collateralAmountChanged.change, debtAmountChanged.change)
      } else if (positiveCollateralChange) {
        await borrow(collateralAmountChanged.change, constants.Zero)
      } else if (positiveDebtAmountChange) {
        await borrow(constants.Zero, debtAmountChanged.change)
      }

      // Repay and withdraw transaction
      const negativeCollateralChange = isNegativeChange(collateralAmountChanged)
      const negativeDebtChange = isNegativeChange(debtAmountChanged)
      console.log({ negativeCollateralChange, negativeDebtChange })
      if (negativeCollateralChange && negativeDebtChange) {
        await repay(collateralAmountChanged.change, debtAmountChanged.change)
      } else if (negativeCollateralChange) {
        await repay(collateralAmountChanged.change, constants.Zero)
      } else if (negativeDebtChange) {
        await repay(constants.Zero, debtAmountChanged.change)
      }
    } catch (err) {
      console.error('manageExisting:onActionClicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  async function onExitClicked() {
    try {
      confirmRepay()
      setIsLoading(true)

      const outstandingCollateral = BigNumber.from(positionCollateralValue)

      if (syntheticBalance.lt(outstandingDebt)) {
        notifyInstance.notification({
          type: 'error',
          message: `You do not have ${getReadableNumber(outstandingDebt)} ${
            synth.synthetic
          } in your wallet`,
        })
        return
      }

      setDebtAmountChanged({ change: outstandingDebt, isPositive: false })
      setCollateralAmountChanged({
        change: outstandingCollateral,
        isPositive: false,
      })

      await arc.repay(positionId, outstandingDebt, outstandingCollateral, synth.name, undefined, {
        gasLimit: 1000000,
      })

      setEnteredCollateralAmount(Zero)
    } catch (err) {
      console.error('ManageExisting:onExitClicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  function confirmRepay() {
    if (
      !window.confirm(
        'If you go under the debt requirement you\'ll lose your arc rewards of the farm. Are you sure you want continue?',
      )
    ) {
      throw Error('Arc: Action denied')
    }
  }

  async function onLiquidateClick() {
    try {
      setIsLoading(true)
      await arc.approveStableShare(liquidationDetails.liquidatorRequirement, synth.name)

      await arc.liquidatePosition(positionId, synth.name, undefined, {
        gasLimit: 700000,
      })
    } catch (err) {
      console.error('ManageExisting:onLiquidateClick', err)
    } finally {
      setIsLoading(false)
    }
  }

  function normalizeValue(value: BigNumber) {
    return value.mul(constants.WeiPerEther).div(BigNumber.from(10).pow(collateralDetails.decimals))
  }

  let defaultSynthetic: BigNumber
  if (debtAmountChanged.change.eq(Zero)) {
    defaultSynthetic = BigNumber.from(position.borrowedAmountValue)
  } else if (debtAmountChanged.isPositive) {
    defaultSynthetic = BigNumber.from(position.borrowedAmountValue).add(debtAmountChanged.change)
  } else {
    defaultSynthetic = BigNumber.from(position.borrowedAmountValue).sub(debtAmountChanged.change)
  }

  return (
    <>
      {confetti && <Confetti numberOfPieces={1000} recycle={false} />}
      <Manage
        title={`Vault #${positionId.toString()}`}
        availableSynths={synths}
        collateralFieldTitle="Collateral"
        syntheticFieldTitle="Debt"
        actionTitle="Update"
        synth={synth}
        positionOwner={position.owner.toLowerCase()}
        onPrimaryClick={onActionClicked}
        onExitClick={onExitClicked}
        onLiquidateClick={
          liquidationDetails && BigNumber.from(liquidationDetails.collateralAtRisk).gt(0)
            ? onLiquidateClick
            : undefined
        }
        changeDescription={getDescription(
          debtAmountChanged,
          {
            ...collateralAmountChanged,
            change: normalizeValue(collateralAmountChanged.change),
          },
          synth,
        )}
        setEnteredCollateralAmount={setEnteredCollateralAmount}
        setEnteredSyntheticAmount={setEnteredDebtAmount}
        currentCollateralRatio={calculateCollateralRatio(
          AssetType.Collateral,
          normalizeValue(enteredCollateralAmount),
          enteredDebtAmount,
          price,
        )}
        currentLiquidationPrice={calculateLiquidationPrice(
          enteredCollateralAmount,
          enteredDebtAmount,
          price,
          synth,
        )
          .mul(BigNumber.from(10).pow(collateralDetails.decimals))
          .div(constants.WeiPerEther)}
        defaultCollateral={positionCollateralValue}
        defaultSynthetic={defaultSynthetic.mul(synth.borrowIndex).div(BASE)}
        maxSyntheticAmount={normalizeValue(
          enteredCollateralAmount.mul(price).div(synth.collateralRatio),
        )}
        isEnoughTokens={
          (!collateralAmountChanged.isPositive ||
            collateralAmountChanged.change.lte(collateralBalance)) &&
          (debtAmountChanged.isPositive || debtAmountChanged.change.lte(syntheticBalance))
        }
        collateralDetails={collateralDetails}
        collateralBalance={collateralBalance}
        debtUnpayable={debtUnpayable}
      />
    </>
  )
}

function logChanges(collateralAmountChanged: AmountChange, debtAmountChanged: AmountChange) {
  console.log(
    `Collat change: ${
      collateralAmountChanged.isPositive
    } ${collateralAmountChanged.change.toString()}`,
  )
  console.log(`Debt change: ${debtAmountChanged.isPositive} ${debtAmountChanged.change.toString()}`)
}

function getChangedValue(newValue: BigNumber, initValue: BigNumberish) {
  if (newValue.gt(initValue)) {
    return {
      change: newValue.sub(initValue),
      isPositive: true,
    }
  } else {
    return {
      change: BigNumber.from(initValue.toString() || 0).sub(newValue),
      isPositive: false,
    }
  }
}

function isPositiveChange(change: AmountChange) {
  return change.isPositive && change.change.gt(0)
}

function isNegativeChange(change: AmountChange) {
  return !change.isPositive && change.change.gt(0)
}
